import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import IntlMessageFormat from "intl-messageformat";
import "@/shared/schema/localised-messages";
import {
  Locale,
  useLanguagePreference,
} from "@/features/language/model/language-preference-store";

type Translations = Record<string, string>;

type TranslationContextValue = {
  t: (key: string, params?: Record<string, unknown>) => string;
  locale: Locale;
  isLoading: boolean;
};

const TranslationContext = createContext<TranslationContextValue | null>(null);

const TRANSLATIONS_BASE_URL =
  import.meta.env.VITE_BACKEND_URL + "/api/translations";

const translationCache: Partial<Record<Locale, Translations>> = {};

export const fetchTranslations = async (
  locale: Locale,
): Promise<Translations> => {
  // In dev, skip the in-memory cache and use Date.now() as cache buster so that
  // newly added translation keys are picked up without restarting the Vite dev server.
  // In production, __BUILD_HASH__ ensures cache invalidation on each deploy.
  if (!import.meta.env.DEV && translationCache[locale]) {
    return translationCache[locale];
  }

  const cacheBuster = import.meta.env.DEV ? Date.now() : __BUILD_HASH__;
  const response = await fetch(
    `${TRANSLATIONS_BASE_URL}?locale=${locale}&v=${cacheBuster}`,
    { headers: { "Accept-Language": locale } },
  );

  if (!response.ok) {
    console.error(`Failed to fetch translations for ${locale}`);
    return {};
  }

  const data: Translations = await response.json();
  translationCache[locale] = data;
  return data;
};

const formatCache = new Map<string, IntlMessageFormat>();

const formatMessage = (
  template: string,
  locale: Locale,
  params?: Record<string, unknown>,
): string => {
  if (!params) {
    return template;
  }

  const cacheKey = `${locale}:${template}`;
  let formatter = formatCache.get(cacheKey);

  if (!formatter) {
    try {
      formatter = new IntlMessageFormat(template, locale);
      formatCache.set(cacheKey, formatter);
    } catch {
      return template;
    }
  }

  try {
    return formatter.format(params) as string;
  } catch {
    return template;
  }
};

/**
 * Module-level singleton for non-React contexts.
 * Set by TranslationProvider on mount and locale change.
 */
let singletonTranslations: Translations = {};
let singletonLocale: Locale = "en";

/**
 * Standalone translation function for use outside React components.
 * Uses the translations loaded by TranslationProvider.
 */
export const translate = (
  key: string,
  params?: Record<string, unknown>,
): string => {
  const template = singletonTranslations[key] ?? key;
  return formatMessage(template, singletonLocale, params);
};

type Props = {
  children: ReactNode;
};

export const TranslationProvider = ({ children }: Props) => {
  const { language } = useLanguagePreference();
  const [translations, setTranslations] = useState<Translations>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      const data = await fetchTranslations(language);
      if (!cancelled) {
        setTranslations(data);
        singletonTranslations = data;
        singletonLocale = language;
        setIsLoading(false);
      }
    };

    load();

    // Pre-fetch the other locale in the background
    const otherLocale: Locale = language === "en" ? "nl" : "en";
    fetchTranslations(otherLocale);

    return () => {
      cancelled = true;
    };
  }, [language]);

  const t = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      const template = translations[key] ?? key;
      return formatMessage(template, language, params);
    },
    [translations, language],
  );

  const value = useMemo(
    () => ({ t, locale: language, isLoading }),
    [t, language, isLoading],
  );

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = (): TranslationContextValue => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error("useTranslation must be used within a TranslationProvider");
  }
  return context;
};
